import React, { useCallback, useEffect, useState } from 'react'
import {
  CCloseButton,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSpinner,
} from '@coreui/react-pro'
import RivalTrackerService from 'src/service/RivalTrackerService'
import LoadError from 'src/views/Vote/LoadError'
import { formatDate, formatTime, initialsOf } from './helpers'

// Right-side panel listing the rivals one member is tracking. Open while `member` is set.
const MemberRivals = ({ member, onClose }) => {
  const [rivals, setRivals] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const load = useCallback(() => {
    if (!member?.user_id) return
    setLoading(true)
    setError(false)
    RivalTrackerService.getMemberRivals(member.user_id)
      .then((res) => {
        if (res.success) setRivals(res.data || [])
        else setError(true)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [member?.user_id])

  useEffect(() => {
    setRivals([])
    load()
  }, [load])

  return (
    <COffcanvas placement="end" visible={!!member} onHide={onClose} backdrop style={{ width: 440 }}>
      <COffcanvasHeader>
        <div className="d-flex align-items-center gap-3 min-w-0">
          <span className="rt-avatar rt-avatar--lg" aria-hidden="true">
            {initialsOf(member?.full_name)}
          </span>
          <div className="min-w-0">
            <COffcanvasTitle className="text-truncate">{member?.full_name}</COffcanvasTitle>
            <div className="text-medium-emphasis small text-truncate">
              {member?.team_name ? `${member.team_name} · ` : ''}
              {member?.rival_count} {Number(member?.rival_count) === 1 ? 'rival' : 'rivals'} tracked
            </div>
          </div>
        </div>
        <CCloseButton className="text-reset" onClick={onClose} />
      </COffcanvasHeader>
      <COffcanvasBody>
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4">
            <CSpinner size="sm" />
            <span>Loading rivals...</span>
          </div>
        ) : error ? (
          <LoadError message="We couldn't load this member's rivals." onRetry={load} />
        ) : rivals.length === 0 ? (
          <div className="rt-muted small py-3">This member is not tracking any rivals now.</div>
        ) : (
          <ul className="rt-rival-list">
            {rivals.map((rival) => (
              <li key={rival.id} className="rt-rival-item">
                <span className="rt-avatar rt-avatar--rival" aria-hidden="true">
                  {initialsOf(rival.team_name || rival.owner_name)}
                </span>
                <div className="min-w-0 flex-grow-1">
                  <div className="fw-semibold text-truncate">
                    {rival.team_name || 'Unnamed team'}
                  </div>
                  {rival.owner_name && (
                    <div className="rt-muted small text-truncate">
                      Managed by {rival.owner_name}
                    </div>
                  )}
                </div>
                <div className="text-end text-nowrap small">
                  <div className="rt-muted">Added</div>
                  <div className="fw-semibold">{formatDate(rival.created_at)}</div>
                  <div className="rt-muted">{formatTime(rival.created_at)}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </COffcanvasBody>
    </COffcanvas>
  )
}

export default MemberRivals
